import React, { useId, useRef } from 'react';

import clsx from 'clsx';

import Delayed from '@core/Delayed';
import { IconButton } from '@core/IconButton';
import VisuallyHidden from '@core/VisuallyHidden';

import { classes, toastRootCss } from './getToastCss';

/** How the dismiss control was activated (viewport uses this for focus management). */
export type ToastDismissActivation = 'keyboard' | 'pointer';

export type ToastProps = {
  title?: string;
  children: React.ReactNode;
  /**
   * Optional leading content (icon, spinner, illustration, etc.).
   * Rendered before the text block; not limited to icons.
   * The prefix track shrink-wraps to the slot (no fixed width), so square icons
   * keep equal width and height without extra horizontal padding from the layout.
   */
  prefixSlot?: React.ReactNode;
  /**
   * Optional trailing content in the main row (e.g. compact actions, tags),
   * before the dismiss control when `onDismiss` is set.
   */
  suffixSlot?: React.ReactNode;
  /**
   * ARIA landmark role. Use `"status"` (default) for informational toasts
   * and `"alert"` for urgent/error messages that should interrupt screen readers.
   * @default "status"
   */
  role?: 'status' | 'alert';
  /**
   * If provided, a dismiss control is shown.
   * Receives how the control was activated so the stack can restore focus (WCAG 2.4.3).
   */
  onDismiss?: (activation: ToastDismissActivation) => void;
  className?: string;
  /**
   * When `true`, wraps the toast body in a focusable accessibility shell (used with
   * {@link ToastProvider} for each visible stacked toast). Requires {@link shellStackedCount}.
   */
  shellTabStop?: boolean;
  /**
   * When `false`, the shell does not use `role="group"` even if {@link shellStackedCount} is
   * `>= 2` (the provider renders a single stack-level group). @default true
   */
  shellApplyGroupRole?: boolean;
  /**
   * Toasts in the stack; drives `role="group"` on the shell when `>= 2` and
   * {@link shellApplyGroupRole} is true. Under {@link ToastProvider}, when a stack-level
   * group wraps multiple toasts, this is passed as `1` so the shell does not duplicate `group`.
   */
  shellStackedCount?: number;
  /** Screen-reader-only stack hint; set when more than one toast is stacked. */
  shellStackHintText?: string;
} & React.ComponentPropsWithoutRef<'div'>;

/**
 * Toast — brief, transient notification (Snackbar-style). Experimental; API may change.
 *
 * `role="status"` / `role="alert"` wraps **only** title and body. Text is deferred one
 * macrotask via {@link Delayed} (`0ms`) so the live region mounts before its text; explicit
 * `aria-live` / `aria-atomic` improve SR reliability. The dismiss control sits outside that
 * subtree in DOM order.
 *
 * For stacked toasts and timers, use with {@link ToastProvider} and {@link useToast}.
 */
const Toast = React.forwardRef<HTMLDivElement, ToastProps>(function Toast(
  props,
  ref
) {
  const {
    title,
    children,
    prefixSlot,
    suffixSlot,
    role = 'status',
    onDismiss,
    className,
    shellTabStop = false,
    shellApplyGroupRole = true,
    shellStackedCount = 1,
    shellStackHintText,
    ...rest
  } = props;

  const dismissPointerRef = useRef(false);
  const baseId = useId();
  const titleId = `${baseId}-toast-title`;
  const descriptionId = `${baseId}-toast-description`;
  const stackHintId = `${baseId}-toast-stack-hint`;

  const labelledBy =
    title !== undefined && title !== null && title !== ''
      ? `${titleId} ${descriptionId}`
      : descriptionId;

  const contentAndDismiss = (
    <>
      <div
        className={clsx(
          classes.content,
          !onDismiss && classes.contentFullPadding
        )}
      >
        {prefixSlot ? <div className={classes.prefix}>{prefixSlot}</div> : null}
        <div className={classes.main}>
          <div className={classes.text}>
            <div
              aria-atomic="true"
              aria-live={role === 'alert' ? 'assertive' : 'polite'}
              className={classes.liveRegion}
              role={role}
            >
              <Delayed waitBeforeShowInMilliSeconds={0}>
                <div>
                  {title ? (
                    <>
                      <p className={classes.title} id={titleId}>
                        {title}
                      </p>
                      {/* Space text node ensures title and description are not
                          run together when a screen reader announces the atomic
                          live region (e.g. "Saved Your changes." not "SavedYour changes.") */}{' '}
                    </>
                  ) : null}
                  <div className={classes.description} id={descriptionId}>
                    {children}
                  </div>
                </div>
              </Delayed>
            </div>
          </div>
          {suffixSlot ? (
            <div className={classes.suffix}>{suffixSlot}</div>
          ) : null}
        </div>
      </div>
      {onDismiss ? (
        <div className={classes.dismiss}>
          <IconButton
            hideTooltip
            data-cds-toast-dismiss="true"
            intent="close"
            size="small"
            variant="ghostSecondary"
            onClick={() => {
              const fromPointer = dismissPointerRef.current;
              dismissPointerRef.current = false;
              onDismiss(fromPointer ? 'pointer' : 'keyboard');
            }}
            onPointerDown={(event) => {
              dismissPointerRef.current =
                event.pointerType === 'mouse' ||
                event.pointerType === 'touch' ||
                event.pointerType === 'pen';
            }}
          />
        </div>
      ) : null}
    </>
  );

  return (
    <div
      ref={ref}
      className={clsx(classes.root, className)}
      css={toastRootCss}
      {...rest}
    >
      {shellTabStop ? (
        <div
          aria-describedby={shellStackHintText ? stackHintId : undefined}
          aria-labelledby={labelledBy}
          className={classes.shell}
          data-cds-toast-shell="true"
          role={
            shellStackedCount >= 2 && shellApplyGroupRole ? 'group' : undefined
          }
          tabIndex={0}
        >
          {contentAndDismiss}
          {shellStackHintText ? (
            <VisuallyHidden component="span" id={stackHintId}>
              {shellStackHintText}
            </VisuallyHidden>
          ) : null}
        </div>
      ) : (
        contentAndDismiss
      )}
    </div>
  );
});

export default Toast;
